import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { inputClass, labelClass } from "@/lib/constants";

const pounds = (p) => (p == null ? "" : (p / 100).toFixed(2));

export default function AdminDelivery() {
  const [settings, setSettings] = useState(null);
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState({ name: "", match_type: "prefix", patterns: "", exclusions: "", fee: "", active: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await api.get("/admin/delivery");
    const s = res.data.settings || {};
    setSettings({
      base_postcode: s.base_postcode || "",
      max_radius_miles: s.max_radius_miles ?? "",
      free_delivery_threshold: pounds(s.free_delivery_threshold_pence),
      min_delivery_order: pounds(s.min_delivery_order_pence),
      excluded_patterns: (s.excluded_patterns || []).join(", "),
    });
    setZones(res.data.zones || []);
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put("/admin/delivery", {
        base_postcode: settings.base_postcode || null,
        max_radius_miles: settings.max_radius_miles === "" ? null : parseFloat(settings.max_radius_miles),
        free_delivery_threshold_pence: settings.free_delivery_threshold === "" ? null : Math.round(parseFloat(settings.free_delivery_threshold) * 100),
        min_delivery_order_pence: settings.min_delivery_order === "" ? null : Math.round(parseFloat(settings.min_delivery_order) * 100),
        excluded_patterns: settings.excluded_patterns.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Delivery settings saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveZone = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/delivery/zones", {
        name: form.name,
        match_type: form.match_type,
        patterns: form.patterns.split(",").map((x) => x.trim()).filter(Boolean),
        exclusions: form.exclusions.split(",").map((x) => x.trim()).filter(Boolean),
        fee_pence: Math.round(parseFloat(form.fee || "0") * 100),
        active: form.active,
        sort_order: zones.length + 1,
      });
      toast.success("Zone saved");
      setForm({ name: "", match_type: "prefix", patterns: "", exclusions: "", fee: "", active: false });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleZone = async (z) => {
    await api.put(`/admin/delivery/zones/${z.id}`, {
      name: z.name, match_type: z.match_type, patterns: z.patterns, exclusions: z.exclusions || [],
      fee_pence: z.fee_pence, active: !z.active, sort_order: z.sort_order,
    }).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const deleteZone = async (z) => {
    if (!window.confirm(`Delete zone "${z.name}"?`)) return;
    await api.delete(`/admin/delivery/zones/${z.id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  if (!settings) return <p className="text-sm text-cream-muted">Loading…</p>;

  return (
    <div data-testid="admin-delivery-page">
      <h1 className="font-serif text-2xl font-bold text-cream-text">Delivery settings</h1>
      <p className="mt-1 text-sm text-cream-muted">
        Delivery is only live for zones marked active. Collection is always available and free.
      </p>

      <section className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="delivery-settings-section">
        <h2 className="font-serif text-lg font-semibold text-cream-text">Area &amp; charges</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label htmlFor="d-base" className={labelClass}>Base postcode</label>
            <input id="d-base" value={settings.base_postcode} onChange={(e) => setSettings((s) => ({ ...s, base_postcode: e.target.value }))} placeholder="e.g. B97 6AA" className={`${inputClass} uppercase`} data-testid="base-postcode-input" />
          </div>
          <div>
            <label htmlFor="d-radius" className={labelClass}>Max radius (miles)</label>
            <input id="d-radius" type="number" step="0.5" min="0" value={settings.max_radius_miles} onChange={(e) => setSettings((s) => ({ ...s, max_radius_miles: e.target.value }))} className={inputClass} data-testid="radius-input" />
          </div>
          <div>
            <label htmlFor="d-min" className={labelClass}>Min delivery order (£)</label>
            <input id="d-min" type="number" step="0.01" min="0" value={settings.min_delivery_order} onChange={(e) => setSettings((s) => ({ ...s, min_delivery_order: e.target.value }))} className={inputClass} data-testid="min-order-input" />
          </div>
          <div>
            <label htmlFor="d-free" className={labelClass}>Free delivery from (£)</label>
            <input id="d-free" type="number" step="0.01" min="0" value={settings.free_delivery_threshold} onChange={(e) => setSettings((s) => ({ ...s, free_delivery_threshold: e.target.value }))} className={inputClass} data-testid="free-threshold-input" />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="d-exclusions" className={labelClass}>Excluded postcodes (comma-separated; end with * for a deliberate prefix)</label>
            <input id="d-exclusions" value={settings.excluded_patterns} onChange={(e) => setSettings((s) => ({ ...s, excluded_patterns: e.target.value }))} placeholder="e.g. B98 9*, WR5 3" className={inputClass} data-testid="exclusions-input" />
          </div>
        </div>
        <button onClick={saveSettings} disabled={busy} className="mt-5 rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover disabled:opacity-50" data-testid="save-delivery-settings-button">
          {busy ? "Saving…" : "Save settings"}
        </button>
      </section>

      <section className="mt-8 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="zones-section">
        <h2 className="font-serif text-lg font-semibold text-cream-text">Postcode zones</h2>
        <div className="mt-4 space-y-3" data-testid="zones-list">
          {zones.length === 0 && <p className="text-sm text-cream-muted">No zones yet.</p>}
          {zones.map((z) => (
            <div key={z.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-choco-line bg-choco p-4" data-testid={`zone-row-${z.id}`}>
              <div>
                <p className="text-sm font-semibold text-cream-text">
                  {z.name} <span className="ml-2 text-xs text-cream-muted">{z.match_type}: {(z.patterns || []).join(", ")} · £{(z.fee_pence / 100).toFixed(2)}</span>
                </p>
                {z.exclusions?.length > 0 && <p className="mt-0.5 text-xs text-cream-muted">Excludes: {z.exclusions.join(", ")}</p>}
                {z.draft && <p className="mt-0.5 text-xs font-semibold text-amber-400">Draft from price list — confirm coverage and fee before activating</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggleZone(z)} className={`rounded-full px-4 py-1.5 text-xs font-bold ${z.active ? "bg-gold/20 text-gold" : "border border-choco-line text-cream-muted"}`} data-testid={`zone-toggle-${z.id}`}>
                  {z.active ? "Active" : "Inactive"}
                </button>
                <button onClick={() => deleteZone(z)} aria-label={`Delete zone ${z.name}`} className="rounded-full p-2 text-cream-muted hover:text-red-400" data-testid={`zone-delete-${z.id}`}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={saveZone} className="mt-6 grid grid-cols-1 items-end gap-3 border-t border-choco-line pt-5 sm:grid-cols-2 lg:grid-cols-6" data-testid="new-zone-form">
          <div className="lg:col-span-2">
            <label htmlFor="z-name" className={labelClass}>Zone name</label>
            <input id="z-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} data-testid="zone-name-input" />
          </div>
          <div>
            <label htmlFor="z-type" className={labelClass}>Match type</label>
            <select id="z-type" value={form.match_type} onChange={(e) => setForm((f) => ({ ...f, match_type: e.target.value }))} className={inputClass} data-testid="zone-match-type-select">
              <option value="outward_code">Exact outward code (B97)</option>
              <option value="area">Postcode area (B, WR, CV)</option>
              <option value="prefix">Deliberate prefix (B97 6*)</option>
            </select>
          </div>
          <div>
            <label htmlFor="z-patterns" className={labelClass}>Patterns (comma-sep)</label>
            <input id="z-patterns" required value={form.patterns} onChange={(e) => setForm((f) => ({ ...f, patterns: e.target.value }))} placeholder="B96, B97, B98" className={inputClass} data-testid="zone-patterns-input" />
          </div>
          <div>
            <label htmlFor="z-fee" className={labelClass}>Fee (£)</label>
            <input id="z-fee" type="number" step="0.01" min="0" required value={form.fee} onChange={(e) => setForm((f) => ({ ...f, fee: e.target.value }))} className={inputClass} data-testid="zone-fee-input" />
          </div>
          <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover" data-testid="add-zone-button">
            <Plus className="h-4 w-4" aria-hidden="true" /> Add zone
          </button>
        </form>
      </section>
    </div>
  );
}
